import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, InputAdornment, Pagination, TextField, Typography } from "@mui/material";
import { COLOR } from "../../../utils/colors";
import { useState } from "react";
import { GridSearchIcon } from "@mui/x-data-grid";
import { AddOutlined, Delete, Edit, EditOutlined } from "@mui/icons-material";
import { image } from "html2canvas/dist/types/css/types/image";
import EditIcon from "../../../components/icons/EditIcon";
import { useIsExpanded, useSharedIsExpanded } from "../_functions";
import { VIEW } from "../../../utils/breakpoint";
import { Link, useNavigate } from "react-router-dom";
import { useDeleteBuildingMutation, useListBuilding } from "../../../hooks/config-page";
import EmptySection from "../../../components/molecules/EmptySection";
import { parseAsInteger, useQueryState } from "nuqs";
import { Building } from "../../../hooks/config-page/index.type";
import { showSuccessToast } from "../../../utils/utils";


export default function ConfigBuildingPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const { isExpanded } = useSharedIsExpanded();
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: true }))
  const { data, refetch } = useListBuilding({
    page: page,
  })
  const navigate = useNavigate();

  const { trigger: submitDelete, isLoading: isDeleting } = useDeleteBuildingMutation({
    onSuccess: () => {
      showSuccessToast('Building deleted successfully')
      setSelectedToDelete(undefined)
      setPage(1)
      refetch()
    }
  })

  const listBuilding = data?.data || []
  const numPages = data?.num_pages || 1

  const [selectedToDelete, setSelectedToDelete] = useState<Building>()

  const handleDelete = (building: Building) => {
    setSelectedToDelete(building)
  }

  const handleConfirmDelete = () => {
    if (!selectedToDelete) return;

    // Call delete API here
    submitDelete({ building_uuid: selectedToDelete.building_uuid })
  }

  const handleEdit = (building: Building) => {
    navigate(`/config/buildings/${building.building_uuid}?edit=true`)
  }

  const handleDetail = (building: Building) => {
    navigate(`/config/buildings/${building.building_uuid}`)
  }

  return (
    <Box sx={(theme) => ({
      paddingY: '34px',
      color: COLOR.neutral[900],
      [theme.breakpoints.up(VIEW.PHONE)]: {
        paddingX: '24px',
      },
      [theme.breakpoints.up(VIEW.TABLET)]: {
        paddingX: '44px',
      }
    })}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 600,
            color: COLOR.neutral[900]
          }}
        >
          Buildings
        </Typography>
        <Box sx={(theme) => ({
          display: 'flex',
          gap: '12px',
          marginTop: '16px',
          [theme.breakpoints.down(VIEW.TABLET)]: {
            flexDirection: 'column'
          }
        })}>
          <TextField
            id="search-store-name"
            variant="outlined"
            size="small"
            placeholder='Search Building'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: 'auto',
              backgroundColor: 'white',
              height: "40px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GridSearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Link to="/config/buildings/create" style={{ textDecoration: 'none' }}>
            <Button variant="outlined" startIcon={<AddOutlined />}>
              New Building
            </Button>
          </Link>
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ marginTop: '24px' }}>
        {listBuilding.map((building, index) => (
          <Grid item xs={12} sm={6} md={4} lg={isExpanded ? 4 : 3} xl={isExpanded ? 3 : 2} key={index}>
            <Box onClick={() => handleDetail(building)} sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.04)',
              backgroundColor: COLOR.white,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)',
              }

            }}>
              <Box>
                {/* <img src="/autove.jpeg" style={{ width: '100%', height: '200px', objectFit: 'cover' }} alt="" /> */}
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>{building.building_name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'end', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: '14px', color: COLOR.neutral[600] }}>Floors: {building.building_levels.length} | Robots: {building.robots.length}</Typography>
                  <Typography sx={{ fontSize: '14px', color: COLOR.neutral[600] }}>{building.building_address || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <IconButton
                    onClick={() => handleEdit(building)}
                    sx={{
                      color: COLOR.gray[400],
                      p: '1px',
                      transition: 'all 0.3s',
                      '&:hover': {
                        color: COLOR.accent.warning
                      }
                    }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(building);
                    }}
                    sx={{
                      color: COLOR.gray[400],
                      p: '1px',
                      transition: 'all 0.3s',
                      '&:hover': {
                        color: COLOR.accent.danger
                      }
                    }}>
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
        {listBuilding.length === 0 && (
          <EmptySection sx={{ width: '100%', flex: 'auto' }} />
        )}

      </Grid>
      {numPages > 1 && (
        <Box sx={{ mt: 2 }}>
          <Pagination
            count={numPages}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={(_, value) => setPage(value)}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!selectedToDelete} onClose={() => setSelectedToDelete(undefined)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this building? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isDeleting} onClick={() => setSelectedToDelete(undefined)} color="primary">
            Cancel
          </Button>
          <Button disabled={isDeleting} onClick={() => handleConfirmDelete()} color="secondary">
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}